import { useEffect, useRef } from 'react';

/**
 * Runs `callback` on an interval while enabled.
 * Defaults to pausing when the document is hidden and to skipping the initial tick.
 */
export function usePolling(
  callback: () => void | Promise<void>,
  intervalMs: number,
  deps: unknown[] = [],
  options: { enabled?: boolean; immediate?: boolean; pauseWhenHidden?: boolean } = {}
) {
  const { enabled = true, immediate = false, pauseWhenHidden = true } = options;
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = () => {
      if (cancelled) return;
      clearTimer();
      if (pauseWhenHidden && document.visibilityState !== 'visible') {
        return;
      }
      timer = setTimeout(tick, intervalMs);
    };

    const tick = async () => {
      if (cancelled) return;
      if (pauseWhenHidden && document.visibilityState !== 'visible') {
        clearTimer();
        return;
      }
      try {
        await savedCallback.current();
      } catch {
        /* swallow — next tick will retry */
      }
      if (!cancelled) {
        schedule();
      }
    };

    const onVisible = () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') {
        if (immediate) {
          clearTimer();
          void tick();
          return;
        }
        schedule();
        return;
      }
      if (pauseWhenHidden) {
        clearTimer();
      }
    };

    document.addEventListener('visibilitychange', onVisible);

    if (immediate && (!pauseWhenHidden || document.visibilityState === 'visible')) {
      void tick();
    } else {
      schedule();
    }

    return () => {
      cancelled = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
